import React, { useEffect } from "react";

import { useForm } from "react-hook-form";

import LoadingWheel from "../Shared/LoadingWheel";
import { SingleLineSettingRow } from "../Shared/SettingsRows";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import { StyledTextInput } from "../Shared/StyledInputs/StyledTextInput";
import { useEmailSettings } from "../../data/emailSettings/useEmailSettings";
import { useEmailSettingsMutations } from "../../data/emailSettings/useEmailSettingsMutations";
import { EmailSettingsUpdate } from './../../models/emailSettings/EmailSettings';

interface EmailSettingsFormFields {
  defaultFromAddress: string;
  defaultReplyAddress: string;
}

interface EmailSettingsProps {
  agencyId: string;
}

export default function EmailSettings({ agencyId }: EmailSettingsProps) {
  const { register, reset, handleSubmit } = useForm<EmailSettingsFormFields>();
  const { emailSettings } = useEmailSettings(agencyId);
  const { update } = useEmailSettingsMutations(agencyId);

  useEffect(() => {
    if (emailSettings.data?.data) {
      reset({
        defaultFromAddress: emailSettings.data.data.defaultFromAddress,
        defaultReplyAddress: emailSettings.data.data.defaultReplyAddress,
      });
    }
  }, [emailSettings.data, reset]);

  const handleUpdate = (value: EmailSettingsFormFields) => {
    if (!emailSettings.data?.data) return;

    const updatedSettings: EmailSettingsUpdate = {
      ...emailSettings.data.data,
      ...value,
      newsletterArticleCategories: emailSettings.data.data.newsletterArticleCategories.map((c) =>
        ({ id: c.id, categoryId: c.categoryId })) || [],
      marketingGroups: emailSettings.data.data.marketingGroups.map((g) =>
        ({ id: g.id, marketingGroupId: g.marketingGroupId, type: g.type })) || [],
      ctaBanners: emailSettings.data.data.ctaBanners.map((b) =>
        ({ id: b.id, ctaBannerId: b.ctaBannerId, order: b.order })) || [],
    };

    update.mutate(updatedSettings);
  }

  return (
    <div className="divide-y space-y-6">
      <div className="-mx-6 px-6">
        <h3 className="text-xl font-medium text-gray-900">
          Email Settings
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure your basic email settings here.
        </p>
      </div>
      <form onSubmit={handleSubmit(handleUpdate)}>
        <div className="mt-4">
          <p>These addresses will be used as the default "From" and "Reply-To" addresses for outgoing emails.</p>
        </div>
        <ul className="border border-gray-200 bg-white rounded-md divide-y divide-gray-200 my-4">
          {emailSettings.isLoading ? (
            <li key="settings-loading" className="py-4 flex justify-center">
              <LoadingWheel width="w-8" height="h-8" />
            </li>
          ) :
            (
              <>
                <SingleLineSettingRow
                  label="Default From Email"
                  value={
                    <StyledTextInput
                      colsClass=""
                      classes="w-full"
                      register={register}
                      registerName="defaultFromAddress"
                    />
                  }
                />
                <SingleLineSettingRow
                  label="Default Reply Email"
                  value={
                    <StyledTextInput
                      colsClass=""
                      classes="w-full"
                      register={register}
                      registerName="defaultReplyAddress"
                    />
                  }
                />
              </>
            )}
        </ul>
        <div className="flex justify-end mt-4">
          {/* @ts-ignore */}
          <ButtonInput label="Save" isSubmit />
        </div>
      </form>
    </div>
  );
}
